import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, pdf } from '@react-pdf/renderer';

const appUrl = process.env.REACT_APP_URL_BACKEND;
const C = { accent: '#e94560', primary: '#1a1a2e', text: '#1a1a2e', textSec: '#5a5f7a', muted: '#8e92a4', border: '#e4e7f0', bg: '#f8f9fc', success: '#10b981', white: '#ffffff' };

const baseStyles = StyleSheet.create({
  page: { paddingTop: 20, paddingHorizontal: 20, paddingBottom: 45, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: C.white },
  header: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, paddingBottom: 6, borderBottom: `2px solid ${C.accent}` },
  headerA5: { flexDirection: 'row', alignItems: 'center', marginBottom: 3, paddingBottom: 4, borderBottom: `2px solid ${C.accent}` },
  logoImage: { width: 100, height: 45, objectFit: 'contain', marginRight: 12 },
  logoImageA5: { width: 70, height: 30, objectFit: 'contain', marginRight: 8 },
  headerTextContainer: { flex: 1, flexDirection: 'column' },
  headerTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.primary, marginBottom: 2 },
  headerTitleA5: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.primary, marginBottom: 1 },
  headerSubtitle: { fontSize: 8, color: C.muted, marginBottom: 1 },
  headerSubtitleA5: { fontSize: 6.5, color: C.muted, marginBottom: 1 },
  headerSection: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.accent, marginTop: 2, backgroundColor: '#fef2f2', padding: '2 8', borderRadius: 3, alignSelf: 'flex-start' },
  headerSectionA5: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.accent, marginTop: 1, backgroundColor: '#fef2f2', padding: '2 6', borderRadius: 2, alignSelf: 'flex-start' },
  footerLine: { position: 'absolute', bottom: 30, left: 20, right: 20, height: 1, backgroundColor: C.border },
  pageNumber: { position: 'absolute', bottom: 12, left: 20, right: 20, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  pageNumberText: { fontSize: 8, color: C.muted },
});

const Header = ({ empresa, sectionLabel, isA5 = false }) => (
  <View style={isA5 ? baseStyles.headerA5 : baseStyles.header}>
    <Image src={empresa?.imagen_sitio ? `${appUrl}${empresa.imagen_sitio}` : "/logo.jpg"} style={isA5 ? baseStyles.logoImageA5 : baseStyles.logoImage} />
    <View style={baseStyles.headerTextContainer}>
      <Text style={isA5 ? baseStyles.headerTitleA5 : baseStyles.headerTitle}>{empresa?.nombre_sitio || 'Catálogo'} - Catálogo</Text>
      <Text style={isA5 ? baseStyles.headerSubtitleA5 : baseStyles.headerSubtitle}>{empresa?.domicilio_sitio || ''}{empresa?.telefonos_sitio ? ` · Tel: ${empresa.telefonos_sitio}` : ''}</Text>
      {sectionLabel ? <Text style={isA5 ? baseStyles.headerSectionA5 : baseStyles.headerSection}>{sectionLabel}</Text> : null}
    </View>
  </View>
);

const Footer = ({ empresa, pageIndex, totalPaginas }) => (
  <>
    <View style={baseStyles.footerLine} fixed />
    <View style={baseStyles.pageNumber} fixed>
      <Text style={baseStyles.pageNumberText}>{empresa?.nombre_sitio || 'Catálogo'} · {new Date().toLocaleDateString('es-AR')}</Text>
      <Text style={baseStyles.pageNumberText}>Página {pageIndex + 1} de {totalPaginas}</Text>
    </View>
  </>
);

const getImgSrc = (p, empresa) => p.imgSrc ? p.imgSrc : empresa?.imagen_sitio ? `${appUrl}${empresa.imagen_sitio}` : "/logo.jpg";

// ═══════════════════════════════════════
// GRID LAYOUT
// ═══════════════════════════════════════
const GridLayout = ({ products, empresa, sectionLabel, cols, showPrice = true }) => {
  const perPage = cols * cols;
  const w = cols === 2 ? '47%' : cols === 3 ? '31%' : '23%';
  const ih = cols === 2 ? (showPrice ? 180 : 210) : cols === 3 ? (showPrice ? 145 : 165) : (showPrice ? 110 : 125);
  const ph = cols === 2 ? 270 : cols === 3 ? 220 : 170;
  const fs = cols === 2 ? 10 : cols === 3 ? 9 : 8;
  const pfs = cols === 2 ? 13 : cols === 3 ? 11 : 9;
  const maxT = cols === 2 ? 50 : cols === 3 ? 40 : 28;
  const gap = cols === 2 ? 14 : cols === 3 ? 12 : 8;

  const s = StyleSheet.create({
    grid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, gap },
    card: { width: w, height: ph, border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    imgBox: { backgroundColor: C.bg, height: ih, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 3 },
    img: { width: '95%', height: '95%', objectFit: 'contain' },
    info: { padding: cols === 2 ? '8 10' : cols === 3 ? '4 8' : '3 5', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderTop: `1px solid ${C.border}`, gap: 1 },
    title: { fontSize: fs, fontFamily: 'Helvetica-Bold', color: C.text, lineHeight: 1.2, maxHeight: cols <= 3 ? 22 : 18, overflow: 'hidden' },
    code: { fontSize: cols >= 3 ? 7.5 : 8, color: C.muted },
    priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 1 },
    price: { fontSize: pfs, fontFamily: 'Helvetica-Bold', color: C.primary },
    priceOffer: { fontSize: pfs, fontFamily: 'Helvetica-Bold', color: C.success },
    priceOld: { fontSize: cols >= 3 ? 7.5 : 8.5, color: C.muted, textDecoration: 'line-through' },
    badge: { fontSize: 6, fontFamily: 'Helvetica-Bold', color: C.white, backgroundColor: C.accent, padding: '1.5 4', borderRadius: 3, position: 'absolute', top: 4, right: 4 },
  });

  const paginas = []; for (let i = 0; i < products.length; i += perPage) paginas.push(products.slice(i, i + perPage));
  return paginas.map((grupo, pi) => (
    <Page key={pi} size="A4" style={baseStyles.page}>
      <Header empresa={empresa} sectionLabel={sectionLabel} />
      <View style={s.grid}>
        {grupo.map((p, i) => {
          const offer = showPrice && p.priceOld && p.price < p.priceOld;
          const t = p.title?.length > maxT ? `${p.title.substring(0, maxT)}...` : p.title;
          return (
            <View key={i} style={s.card}>
              <View style={s.imgBox}>
                <Image src={getImgSrc(p, empresa)} style={s.img} />
                {offer && <Text style={s.badge}>OFERTA</Text>}
              </View>
              <View style={s.info}>
                <View><Text style={s.title}>{t}</Text><Text style={s.code}>COD: {p.code}</Text></View>
                {showPrice && (
                  <View style={s.priceRow}>
                    {offer ? (<><Text style={s.priceOffer}>{p.price}</Text><Text style={s.priceOld}>{p.priceOld}</Text></>) : (<Text style={s.price}>{p.price || p.priceOld}</Text>)}
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </View>
      <Footer empresa={empresa} pageIndex={pi} totalPaginas={paginas.length} />
    </Page>
  ));
};

// ═══════════════════════════════════════
// LIST WITH IMAGE
// ═══════════════════════════════════════
const ListImgLayout = ({ products, empresa, sectionLabel, rowsPerPage, showPrice = true }) => {
  const imgH = rowsPerPage <= 8 ? 55 : 40;
  const rowH = rowsPerPage <= 8 ? 65 : 50;
  const fs = rowsPerPage <= 8 ? 9 : 8;

  const s = StyleSheet.create({
    list: { marginTop: 8, gap: 4 },
    row: { flexDirection: 'row', alignItems: 'center', border: `1px solid ${C.border}`, borderRadius: 4, padding: 4, height: rowH, gap: 8 },
    imgBox: { width: imgH, height: imgH, backgroundColor: C.bg, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2 },
    img: { width: '90%', height: '90%', objectFit: 'contain' },
    info: { flex: 1, flexDirection: 'column', justifyContent: 'center', gap: 2 },
    topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    title: { fontSize: fs, fontFamily: 'Helvetica-Bold', color: C.text, maxWidth: showPrice ? '70%' : '85%' },
    code: { fontSize: 7, color: C.muted },
    price: { fontSize: fs + 1, fontFamily: 'Helvetica-Bold', color: C.primary },
    priceOffer: { fontSize: fs + 1, fontFamily: 'Helvetica-Bold', color: C.success },
    priceOld: { fontSize: 7, color: C.muted, textDecoration: 'line-through' },
    badge: { fontSize: 6, fontFamily: 'Helvetica-Bold', color: C.white, backgroundColor: C.accent, padding: '1 4', borderRadius: 2 },
  });

  const paginas = []; for (let i = 0; i < products.length; i += rowsPerPage) paginas.push(products.slice(i, i + rowsPerPage));
  return paginas.map((grupo, pi) => (
    <Page key={pi} size="A4" style={baseStyles.page}>
      <Header empresa={empresa} sectionLabel={sectionLabel} />
      <View style={s.list}>
        {grupo.map((p, i) => {
          const offer = showPrice && p.priceOld && p.price < p.priceOld;
          const t = p.title?.length > (showPrice ? 55 : 70) ? `${p.title.substring(0, showPrice ? 55 : 70)}...` : p.title;
          return (
            <View key={i} style={s.row}>
              <View style={s.imgBox}><Image src={getImgSrc(p, empresa)} style={s.img} /></View>
              <View style={s.info}>
                <View style={s.topRow}>
                  <Text style={s.title}>{t}</Text>
                  {offer && <Text style={s.badge}>OFERTA</Text>}
                </View>
                <View style={s.topRow}>
                  <Text style={s.code}>COD: {p.code}</Text>
                  {showPrice && (
                    <View style={{ alignItems: 'flex-end' }}>
                      {offer ? (<><Text style={s.priceOffer}>{p.price}</Text><Text style={s.priceOld}>{p.priceOld}</Text></>) : (<Text style={s.price}>{p.price || p.priceOld}</Text>)}
                    </View>
                  )}
                </View>
              </View>
            </View>
          );
        })}
      </View>
      <Footer empresa={empresa} pageIndex={pi} totalPaginas={paginas.length} />
    </Page>
  ));
};

// ═══════════════════════════════════════
// LIST TEXT ONLY (A5 / A4)
// ═══════════════════════════════════════
const ListTextLayout = ({ products, empresa, sectionLabel, rowsPerPage, pageSize = 'A4', showPrice = true }) => {
  const isA5 = pageSize === 'A5';
  const fs = isA5 ? 7 : 8.5;
  const rh = isA5 ? 15 : 20;
  const headerFs = isA5 ? 6 : 7;
  const maxT = isA5 ? (showPrice ? 45 : 55) : (showPrice ? 65 : 80);

  const s = StyleSheet.create({
    pageA5: { paddingTop: 15, paddingHorizontal: 15, paddingBottom: 421, fontSize: 9, fontFamily: 'Helvetica', backgroundColor: C.white },
    table: { marginTop: isA5 ? 2 : 3 },
    headerRow: { flexDirection: 'row', backgroundColor: C.primary, borderRadius: 3, padding: isA5 ? '3 4' : '4 6', marginBottom: isA5 ? 1 : 2 },
    headerText: { fontSize: headerFs, fontFamily: 'Helvetica-Bold', color: C.white, textTransform: 'uppercase', letterSpacing: 0.5 },
    row: { flexDirection: 'row', alignItems: 'center', padding: isA5 ? '2 4' : '3 6', height: rh, borderBottom: `0.5px solid ${C.border}` },
    rowAlt: { flexDirection: 'row', alignItems: 'center', padding: isA5 ? '2 4' : '3 6', height: rh, borderBottom: `0.5px solid ${C.border}`, backgroundColor: C.bg },
    colCode: { width: showPrice ? '15%' : '20%' },
    colName: { width: showPrice ? '55%' : '80%' },
    colPrice: { width: '15%', textAlign: 'right' },
    colOffer: { width: '15%', textAlign: 'right' },
    cellText: { fontSize: fs, color: C.text },
    cellCode: { fontSize: fs, color: C.muted, fontFamily: 'Helvetica-Bold' },
    cellPrice: { fontSize: fs, fontFamily: 'Helvetica-Bold', color: C.primary, textAlign: 'right' },
    cellPriceOffer: { fontSize: fs, fontFamily: 'Helvetica-Bold', color: C.success, textAlign: 'right' },
    cellPriceOld: { fontSize: fs - 1, color: C.muted, textDecoration: 'line-through', textAlign: 'right' },
    halfLine: { position: 'absolute', top: 421, left: 15, right: 15, height: 0.5, backgroundColor: C.border },
  });

  const paginas = []; for (let i = 0; i < products.length; i += rowsPerPage) paginas.push(products.slice(i, i + rowsPerPage));
  return paginas.map((grupo, pi) => (
    <Page key={pi} size="A4" style={isA5 ? s.pageA5 : baseStyles.page}>
      <Header empresa={empresa} sectionLabel={sectionLabel} isA5={isA5} />
      <View style={s.table}>
        <View style={s.headerRow}>
          <Text style={[s.headerText, s.colCode]}>Código</Text>
          <Text style={[s.headerText, s.colName]}>Descripción</Text>
          {showPrice && <Text style={[s.headerText, s.colPrice]}>Precio</Text>}
          {showPrice && <Text style={[s.headerText, s.colOffer]}>Oferta</Text>}
        </View>
        {grupo.map((p, i) => {
          const offer = showPrice && p.priceOld && p.price < p.priceOld;
          const t = p.title?.length > maxT ? `${p.title.substring(0, maxT)}...` : p.title;
          return (
            <View key={i} style={i % 2 === 0 ? s.row : s.rowAlt}>
              <Text style={[s.cellCode, s.colCode]}>{p.code}</Text>
              <Text style={[s.cellText, s.colName]}>{t}</Text>
              {showPrice && <Text style={[offer ? s.cellPriceOld : s.cellPrice, s.colPrice]}>{p.priceOld || p.price}</Text>}
              {showPrice && <Text style={[s.cellPriceOffer, s.colOffer]}>{offer ? p.price : ''}</Text>}
            </View>
          );
        })}
      </View>
      {isA5 && <View style={s.halfLine} fixed />}
      {!isA5 && <Footer empresa={empresa} pageIndex={pi} totalPaginas={paginas.length} />}
    </Page>
  ));
};

// ═══════════════════════════════════════
// MAIN
// ═══════════════════════════════════════
const ExportPdf = ({ products, empresa, seccion, ramo, rubro, subrubro, format = { type: 'grid', cols: 4, showPrice: true } }) => {
  const sectionLabel = [seccion, ramo, rubro, subrubro].filter(Boolean).join(' › ');
  const showPrice = format.showPrice !== false;
  return (
    <Document>
      {format.type === 'grid' && <GridLayout products={products} empresa={empresa} sectionLabel={sectionLabel} cols={format.cols || 4} showPrice={showPrice} />}
      {format.type === 'list-img' && <ListImgLayout products={products} empresa={empresa} sectionLabel={sectionLabel} rowsPerPage={format.rows || 8} showPrice={showPrice} />}
      {format.type === 'list-text' && <ListTextLayout products={products} empresa={empresa} sectionLabel={sectionLabel} rowsPerPage={format.rows || 35} pageSize={format.pageSize || 'A4'} showPrice={showPrice} />}
    </Document>
  );
};

export const handleOpenPdf = async (products, empresa, seccion, ramo, rubro, subrubro, format) => {
  const blob = await pdf(<ExportPdf products={products} empresa={empresa} seccion={seccion} ramo={ramo} rubro={rubro} subrubro={subrubro} format={format} />).toBlob();
  window.open(URL.createObjectURL(blob), '_blank');
};

export default ExportPdf;